import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Alert,
  Input,
  Upload,
  Table,
  Modal,
  message,
  Typography,
  Space,
  Row,
  Col,
  Tag,
  Statistic,
  Checkbox,
} from 'antd';
import {
  UploadOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  InboxOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import type { UploadProps, TableColumnsType } from 'antd';
import { useAuth0 } from '@auth0/auth0-react';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Dragger } = Upload;

// Declare global constants injected by Vite define
declare const API_BASE_URL: string;

// Types
interface ICD10NTAPoints {
  id: string;
  code: string;
  nta_points: number;
}

interface ICD10NTAVersion {
  id: string;
  version_number: number;
  upload_status: string;
  uploaded_by_user_id: string;
  uploaded_by_name?: string;
  uploaded_by_email?: string;
  uploaded_at: string;
  activated_at?: string;
  archived_at?: string;
  row_count: number;
  file_name?: string;
  notes?: string;
}

interface UploadResponse {
  version_id: string;
  version_number: number;
  row_count: number;
  duplicates_ignored: number;
  warning?: string;
}

const ICD10NTAPointsPage: React.FC = () => {
  const [versions, setVersions] = useState<ICD10NTAVersion[]>([]);
  const [activeVersion, setActiveVersion] = useState<ICD10NTAVersion | null>(null);
  const [pointsCount, setPointsCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadNotes, setUploadNotes] = useState('');
  const [hasHeader, setHasHeader] = useState(false);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorDetails, setErrorDetails] = useState<string>('');
  const [pendingUpload, setPendingUpload] = useState<UploadResponse | null>(null);
  const { getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();

  // Load data on mount
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const token = await getAccessTokenSilently();
      const headers = { Authorization: `Bearer ${token}` };
      
      // Fetch all data in parallel
      const [versionsRes, activeRes, countRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/admin/data/icd10-nta-points/versions`, { headers }).then(r => r.json()),
        fetch(`${API_BASE_URL}/api/admin/data/icd10-nta-points/versions/active`, { headers }).then(r => r.json()),
        fetch(`${API_BASE_URL}/api/admin/data/icd10-nta-points/points/count`, { headers }).then(r => r.json()),
      ]);

      setVersions(versionsRes);
      setActiveVersion(activeRes);
      setPointsCount(countRes.count || 0);
    } catch (error: any) {
      console.error('Error loading data:', error);
      message.error('Failed to load data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!uploadFile) return;

    setIsUploading(true);
    try {
      const token = await getAccessTokenSilently();
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('has_header', hasHeader.toString());
      if (uploadNotes) {
        formData.append('notes', uploadNotes);
      }

      const response = await fetch(`${API_BASE_URL}/api/admin/data/icd10-nta-points/upload`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json();
        const errorMessage = error.detail || 'Upload failed';
        
        // Show detailed error in modal for validation and file errors
        if (errorMessage.includes('Error:') || errorMessage.includes('row') || errorMessage.includes('column') || errorMessage.includes('CSV')) {
          setErrorDetails(errorMessage);
          setShowErrorModal(true);
        } else {
          message.error(errorMessage);
        }
        return;
      }

      const data: UploadResponse = await response.json();

      if (data.warning) {
        setPendingUpload(data);
        setShowWarningModal(true);
      } else {
        let successMessage = `Version ${data.version_number} uploaded with ${data.row_count.toLocaleString()} rows`;
        if (data.duplicates_ignored > 0) {
          successMessage += ` (${data.duplicates_ignored.toLocaleString()} duplicates ignored)`;
        }
        message.success(successMessage);
        resetUploadForm();
        await loadData();
      }
    } catch (error: any) {
      console.error('Upload error:', error);
      message.error('An unexpected error occurred during upload. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const confirmUploadWithWarning = async () => {
    setShowWarningModal(false);
    if (pendingUpload) {
      let successMessage = `Version ${pendingUpload.version_number} uploaded with ${pendingUpload.row_count.toLocaleString()} rows`;
      if (pendingUpload.duplicates_ignored > 0) {
        successMessage += ` (${pendingUpload.duplicates_ignored.toLocaleString()} duplicates ignored)`;
      }
      message.success(successMessage);
      resetUploadForm();
      await loadData();
    }
  };

  const resetUploadForm = () => {
    setUploadFile(null);
    setUploadNotes('');
    setHasHeader(false);
    setPendingUpload(null);
    setErrorDetails('');
    setShowErrorModal(false);
  };

  const handleActivate = async (version: ICD10NTAVersion) => {
    Modal.confirm({
      title: 'Activate Version?',
      content: `Are you sure you want to activate version ${version.version_number}? This will replace all current ICD-10 NTA Points data.`,
      okText: 'Activate',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          const token = await getAccessTokenSilently();
          const response = await fetch(`${API_BASE_URL}/api/admin/data/icd10-nta-points/activate`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ version_id: version.id }),
          });

          if (!response.ok) {
            const error = await response.json();
            throw new Error(error.detail || 'Activation failed');
          }

          message.success(`Version ${version.version_number} activated successfully`);
          await loadData();
        } catch (error: any) {
          console.error('Activation error:', error);
          message.error(error.message || 'Failed to activate version');
        }
      },
    });
  };

  const getStatusTag = (status: string) => {
    const config: Record<string, { color: string; icon: React.ReactNode }> = {
      active: { color: 'success', icon: <CheckCircleOutlined /> },
      pending: { color: 'processing', icon: <ClockCircleOutlined /> },
      archived: { color: 'default', icon: <InboxOutlined /> },
      failed: { color: 'error', icon: <WarningOutlined /> },
    };

    const { color, icon } = config[status] || { color: 'default', icon: null };

    return (
      <Tag color={color}>
        {icon} {status.charAt(0).toUpperCase() + status.slice(1)}
      </Tag>
    );
  };

  const columns: TableColumnsType<ICD10NTAVersion> = [
    {
      title: 'Version',
      dataIndex: 'version_number',
      key: 'version_number',
      render: (version: number) => <strong>v{version}</strong>,
    },
    {
      title: 'Status',
      dataIndex: 'upload_status',
      key: 'upload_status',
      render: (status: string) => getStatusTag(status),
    },
    {
      title: 'Rows',
      dataIndex: 'row_count',
      key: 'row_count',
      render: (count: number) => count.toLocaleString(),
    },
    {
      title: 'Uploaded By',
      key: 'uploaded_by',
      render: (_: any, record: ICD10NTAVersion) => (
        <div>
          <div>{record.uploaded_by_name}</div>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.uploaded_by_email}
          </Text>
        </div>
      ),
    },
    {
      title: 'Uploaded At',
      dataIndex: 'uploaded_at',
      key: 'uploaded_at',
      render: (date: string) => formatDistanceToNow(new Date(date)) + ' ago',
    },
    {
      title: 'Notes',
      dataIndex: 'notes',
      key: 'notes',
      ellipsis: true,
      render: (notes: string) => notes || '-',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: ICD10NTAVersion) => (
        <Space>
          <Button 
            type="default"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => navigate(`/admin/data/icd10-nta-points/viewer?version=${record.id}`)}
          >
            View Data
          </Button>
          {(record.upload_status === 'pending' || record.upload_status === 'failed') && (
            <Button type="primary" size="small" onClick={() => handleActivate(record)}>
              {record.upload_status === 'failed' ? 'Retry Activate' : 'Activate'}
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const uploadProps: UploadProps = {
    name: 'file',
    accept: '.csv',
    beforeUpload: (file) => {
      if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
        message.error('Please select a CSV file');
        return false;
      }
      setUploadFile(file);
      return false; // Prevent automatic upload
    },
    onRemove: () => {
      setUploadFile(null);
    },
    fileList: uploadFile ? [uploadFile as any] : [],
  };

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <Title level={2} style={{ margin: 0 }}>ICD-10 NTA Points</Title>
        <Button
          type="primary"
          icon={<EyeOutlined />}
          onClick={() => navigate('/admin/data/icd10-nta-points/viewer')}
          style={{ backgroundColor: '#1890ff' }}
        >
          View Current Data
        </Button>
      </div>

      {/* Status Cards */}
      <Row gutter={16} style={{ marginBottom: '24px' }}>
        <Col span={8}>
          <Card>
            <Statistic
              title="Active Version"
              value={activeVersion ? `v${activeVersion.version_number}` : 'None'}
              prefix={activeVersion && <CheckCircleOutlined />}
            />
            {activeVersion && (
              <Text type="secondary" style={{ fontSize: '12px' }}>
                Activated {formatDistanceToNow(new Date(activeVersion.activated_at!))} ago
              </Text>
            )}
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="Total ICD-10 NTA Points"
              value={pointsCount}
              formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="Expected Row Count"
              value="~2,150"
              valueStyle={{ color: '#3f8600' }}
            />
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Typical range: 2,000 - 2,300 rows
            </Text>
          </Card>
        </Col>
      </Row>

      {/* Upload Section */}
      <Card
        title="Upload New ICD-10 NTA Points Data"
        style={{ marginBottom: '24px' }}
      >
        <Alert
          message="Important"
          description="Uploading will replace all existing ICD-10 NTA Points data. The file should contain approximately 2,150 rows and must have at least 1,800 rows."
          type="warning"
          showIcon
          style={{ marginBottom: '16px' }}
        />
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div>
            <Text strong>Select CSV File</Text>
            <Paragraph type="secondary">
              Upload a CSV file with the first 2 columns as: Code, NTA Points (in that order).
              Any additional columns beyond the first 2 will be ignored.
            </Paragraph>
            <Dragger {...uploadProps}>
              <p className="ant-upload-drag-icon">
                <UploadOutlined />
              </p>
              <p className="ant-upload-text">Click or drag CSV file to this area to upload</p>
              <p className="ant-upload-hint">
                The file must have at least 2 columns and 1,800 rows
              </p>
            </Dragger>
          </div>

          <div>
            <Checkbox
              checked={hasHeader}
              onChange={(e) => setHasHeader(e.target.checked)}
              disabled={isUploading}
            >
              File has header row (skip first row)
            </Checkbox>
          </div>

          {uploadFile && (
            <div>
              <Text strong>Upload Notes (Optional)</Text>
              <Input.TextArea
                placeholder="Add any notes about this upload..."
                value={uploadNotes}
                onChange={(e) => setUploadNotes(e.target.value)}
                rows={3}
                disabled={isUploading}
              />
            </div>
          )}

          {uploadFile && (
            <Button
              type="primary"
              onClick={handleUpload}
              loading={isUploading}
              icon={<UploadOutlined />}
              size="large"
            >
              Upload File
            </Button>
          )}
        </Space>
      </Card>

      {/* Version History */}
      <Card
        title="Version History"
        loading={isLoading}
      >
        <Table
          columns={columns}
          dataSource={versions}
          rowKey="id"
          pagination={{
            pageSize: 10,
            showSizeChanger: false,
          }}
        />
      </Card>

      {/* Warning Modal */}
      <Modal
        title="Row Count Warning"
        open={showWarningModal}
        onOk={confirmUploadWithWarning}
        onCancel={() => {
          setShowWarningModal(false);
          setPendingUpload(null);
        }}
        okText="Continue with Upload"
        cancelText="Cancel"
      >
        {pendingUpload && (
          <>
            <Alert
              message="Unusual Row Count Detected"
              description={pendingUpload.warning}
              type="warning"
              showIcon
              style={{ marginBottom: '16px' }}
            />
            <p>
              <strong>File:</strong> {uploadFile?.name}
            </p>
            <p>
              <strong>Rows:</strong> {pendingUpload.row_count.toLocaleString()}
            </p>
            <p>
              <strong>Expected:</strong> ~2,150 rows (typical range: 2,000 - 2,300)
            </p>
            <p>Are you sure you want to continue with this upload?</p>
          </>
        )}
      </Modal>

      {/* Error Details Modal */}
      <Modal
        title={
          <Space>
            <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
            Upload Error
          </Space>
        }
        open={showErrorModal}
        onOk={() => setShowErrorModal(false)}
        onCancel={() => setShowErrorModal(false)}
        okText="Close"
        cancelButtonProps={{ style: { display: 'none' } }}
        width={600}
      >
        <div style={{ marginBottom: '16px' }}>
          <Text strong>There was an issue with your CSV file:</Text>
        </div>
        <Alert
          message="Validation Error"
          description={
            <div style={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
              {errorDetails}
            </div>
          }
          type="error"
          showIcon
        />
        <div style={{ marginTop: '16px' }}>
          <Text type="secondary">
            Please fix the issue in your CSV file and try uploading again.
          </Text>
          <div style={{ marginTop: '12px', padding: '12px', backgroundColor: '#f6f6f6', borderRadius: '4px' }}>
            <Text strong style={{ fontSize: '13px' }}>Common fixes:</Text>
            <ul style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#666' }}>
              <li>Ensure your file has at least 2 columns: Code, NTA Points</li>
              <li>Check that no cells in the first 2 columns are empty</li>
              <li>Verify that NTA Points column contains valid numbers</li>
              <li>If your file has column headers, check the "File has header row" option</li>
              <li>Make sure the file is saved as a proper CSV format</li>
              <li>Verify the file contains at least 1,800 data rows</li>
            </ul>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ICD10NTAPointsPage; 